import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import * as LucideIcons from 'lucide-react';
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  Phone,
  PhoneIncoming,
  PhoneOff,
  Mic,
  MicOff,
  Pause,
  Play,
  Activity as ActivityIcon,
} from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { useTwilioDevice } from '@/hooks/useTwilioDevice';
import { useRealtimeBuffer } from '@/hooks/useRealtimeBuffer';
import { useAgentAudioCapture } from '@/hooks/useAgentAudioCapture';
import { LiveCoachingPanel } from '@/components/agent-console/LiveCoachingPanel';
import { CallConversionGauges } from '@/components/captain-overview/CallConversionGauges';
import { MakeCallModal } from '@/components/calling/MakeCallModal';
import { usePermissions } from '@/hooks/usePermissions';
import { getRoutePermissions } from '@/config/routePermissions';

interface NavItem {
  to: string;
  label: string;
  icon: any;
  badge?: string;
}

interface PageStatusDB {
  id: string;
  route: string;
  status: 'red' | 'yellow' | 'green';
  label: string | null;
  visible: boolean;
  category_id: string | null;
  order: number;
  icon: string | null;
}

interface MenuCategoryDB {
  id: string;
  name: string;
  emoji: string;
  order: number;
  visible: boolean;
}

interface NavCategory {
  title: string;
  items: NavItem[];
}

interface TranscriptChunk {
  id: string;
  speaker: 'agent' | 'customer';
  text: string;
  timestamp: string;
  final: boolean;
  confidence?: number;
  callSid?: string;
}

interface LiveSuggestion {
  id: string;
  type: 'script' | 'objection' | 'upsell' | 'compliance' | 'info';
  severity: 'critical' | 'warning' | 'suggestion' | 'info';
  title: string;
  message: string;
  suggestedScript?: string;
  timestamp: number;
}

// Helper function to get Lucide icon component by name
const getLucideIcon = (iconName: string | null): any => {
  if (!iconName) return ActivityIcon; // Default fallback icon

  // Handle common variations and fallbacks
  const iconMap: Record<string, string> = {
    'Activity': 'Activity',
    'Settings': 'Settings',
    'SettingsIcon': 'Settings',
  };

  const mappedName = iconMap[iconName] || iconName;
  const IconComponent = (LucideIcons as any)[mappedName];

  return IconComponent || ActivityIcon; // Fallback to Activity icon if not found
};

// Create default menu structure when database tables don't exist
const createDefaultMenuStructure = async () => {
  try {
    console.log('[AdminLayout] Creating default menu structure...');

    // Create default categories
    const defaultCategories = [
      { name: 'Dashboard & System', emoji: '🏠', order: 1 },
      { name: 'Contact Center', emoji: '📞', order: 2 },
      { name: 'AI Voice Agents', emoji: '🤖', order: 3 },
      { name: 'Customers', emoji: '👥', order: 4 },
      { name: 'Settings', emoji: '⚙️', order: 5 },
    ];

    for (const category of defaultCategories) {
      const { error: catError } = await supabase
        .from('menu_categories')
        .upsert({
          name: category.name,
          emoji: category.emoji,
          order: category.order,
          visible: true
        });

      if (catError) {
        console.error('Error creating category:', category.name, catError);
      }
    }

    // Get created categories
    const { data: categories } = await supabase
      .from('menu_categories')
      .select('id, name')
      .order('order');

    if (!categories) return;

    // Create default pages
    const defaultPages = [
      // Dashboard & System
      { route: '/dashboard', label: 'Dashboard', icon: 'Activity', category_name: 'Dashboard & System', status: 'green' },
      { route: '/system-overview', label: 'System Overview', icon: 'Settings', category_name: 'Dashboard & System', status: 'green' },

      // Contact Center
      { route: '/contact-center/agent-console', label: 'Agent Console', icon: 'Phone', category_name: 'Contact Center', status: 'green' },
      { route: '/contact-center/queue-management', label: 'Queue Management', icon: 'ListChecks', category_name: 'Contact Center', status: 'green' },

      // AI Voice Agents
      { route: '/ai-voice-agents/phone-numbers', label: 'Phone Numbers', icon: 'Phone', category_name: 'AI Voice Agents', status: 'green' },
      { route: '/ai-voice-agents/knowledge-base', label: 'Knowledge Base', icon: 'BookOpen', category_name: 'AI Voice Agents', status: 'green' },

      // Settings
      { route: '/settings/page-status', label: 'Menu Management', icon: 'Settings', category_name: 'Settings', status: 'green' },
    ];

    for (const page of defaultPages) {
      const category = categories.find(c => c.name === page.category_name);
      if (!category) continue;

      const { error: pageError } = await supabase
        .from('page_status')
        .upsert({
          route: page.route,
          label: page.label,
          icon: page.icon,
          category_id: category.id,
          status: page.status as 'red' | 'yellow' | 'green',
          visible: true,
          order: 10
        });

      if (pageError) {
        console.error('Error creating page:', page.route, pageError);
      }
    }

    console.log('[AdminLayout] Default menu structure created successfully');
  } catch (error) {
    console.error('[AdminLayout] Error creating default menu structure:', error);
  }
};

const AdminLayout = () => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set()); // Start with all categories collapsed
  const [pageStatuses, setPageStatuses] = useState<Map<string, 'red' | 'yellow' | 'green'>>(new Map());
  const [navCategories, setNavCategories] = useState<NavCategory[]>([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(true);
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission, isAdmin, isLoading: permissionsLoading } = usePermissions();
  const [deviceState, deviceActions] = useTwilioDevice();
  const [agentInfo, setAgentInfo] = useState({ 
    id: '', 
    name: '', 
    status: 'available' as 'available' | 'busy' | 'away' | 'break' | 'meal' | 'meeting' | 'training' | 'active_on_inbound_call' | 'active_on_outbound_call' | 'post_work_time' | 'dialing'
  });
  const [agentQueues, setAgentQueues] = useState<Array<{ id: string; name: string; company_name: string; is_active: boolean }>>([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showMakeCallModal, setShowMakeCallModal] = useState(false);
  const [isInPostWork, setIsInPostWork] = useState(false);
  const [showOutcomeSelection, setShowOutcomeSelection] = useState(false);
  const [postWorkCallInfo, setPostWorkCallInfo] = useState<{
    callSid: string;
    direction: 'inbound' | 'outbound';
    startTime: number;
  } | null>(null);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const previousCallStateRef = useRef<string>('idle'); // Track previous call state
  
  // Get CallSids for realtime subscriptions - memoized to prevent reconnection loops
  // Extract the actual CallSid values first to avoid object reference changes
  const parentSid =
    deviceState.activeCall?.parameters?.ParentCallSid ||
    (deviceState.activeCall?.customParameters as any)?.get?.('ParentCallSid') ||
    null;

  const childSid =
    deviceState.activeCall?.parameters?.CallSid ||
    (deviceState.activeCall?.customParameters as any)?.get?.('CallSid') ||
    deviceState.incomingCallSid ||
    null;

  // Memoize based on the actual string values, not object references
  const callSids = useMemo(() => {
    return Array.from(
      new Set([parentSid, childSid].filter(Boolean))
    ) as string[];
  }, [parentSid, childSid]);

  // Use realtime buffer hook for transcripts and coaching suggestions
  const {
    transcripts: realtimeTranscripts,
    suggestions: liveSuggestions,
    isConnected: isRealtimeConnected,
    error: realtimeError,
    clearBuffers
  } = useRealtimeBuffer(callSids, {
    transcriptCap: 400,
    suggestionCap: 100,
    batchMs: 150
  });

  // Capture agent audio from browser microphone
  useAgentAudioCapture({
    activeCall: deviceState.activeCall,
    callSid: parentSid || childSid || null,
    isCallActive: deviceState.callState === 'connected',
  });

  // Clear buffers when new call comes in
  useEffect(() => {
    if (deviceState.callState === 'ringing') {
      clearBuffers();
    }
  }, [deviceState.callState, clearBuffers]);

  // Automatic status changes based on call state
  useEffect(() => {
    const mapUiToDbStatus = (s: typeof agentInfo.status): 'available' | 'busy' | 'away' | 'break' | 'meal' | 'meeting' | 'training' => {
      // Ephemeral/extended UI states are persisted as 'busy'
      if (s === 'dialing' || s === 'active_on_inbound_call' || s === 'active_on_outbound_call' || s === 'post_work_time') {
        return 'busy';
      }
      return s as any;
    };

    const updateAgentStatus = async (newStatus: typeof agentInfo.status) => {
      if (!user || !agentInfo.id) return;
      
      console.log('[AdminLayout] Auto-updating status to:', newStatus);
      const dbStatus = mapUiToDbStatus(newStatus);
      
      await supabase
        .from('agents')
        .update({ current_status: dbStatus, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      
      setAgentInfo(prev => ({ ...prev, status: newStatus }));
    };

    const previousState = previousCallStateRef.current;
    const currentState = deviceState.callState;
    
    console.log('[AdminLayout] Call state transition:', previousState, '->', currentState);

    // When call connects, set status to active (inbound or outbound)
    if (currentState === 'connected' && previousState !== 'connected' && !isInPostWork) {
      console.log('[AdminLayout] Call connected - extracting call info...');
      console.log('[AdminLayout] Full deviceState:', JSON.stringify({
        status: deviceState.status,
        callState: deviceState.callState,
        hasActiveCall: !!deviceState.activeCall,
        activeCallParams: deviceState.activeCall?.parameters,
        incomingCallSid: deviceState.incomingCallSid
      }, null, 2));
      
      // Try multiple sources for callSid with priority order
      const params = (deviceState.activeCall?.parameters as any) || {};
      const direction = params?.Direction as string | undefined;
      
      // Priority order for callSid extraction:
      // 1. For inbound calls, use incomingCallSid first (most reliable for inbound)
      // 2. Then try CallSid from parameters
      // 3. Then try custom parameters
      let callSid: string | undefined;
      
      if (direction?.toLowerCase() === 'inbound' && deviceState.incomingCallSid) {
        callSid = deviceState.incomingCallSid;
        console.log('[AdminLayout] Using incomingCallSid for inbound call:', callSid);
      } else if (params.CallSid) {
        callSid = params.CallSid as string;
        console.log('[AdminLayout] Using CallSid from parameters:', callSid);
      } else if (deviceState.activeCall?.customParameters) {
        callSid = (deviceState.activeCall.customParameters as any).get?.('CallSid');
        console.log('[AdminLayout] Using CallSid from customParameters:', callSid);
      } else if (deviceState.incomingCallSid) {
        // Fallback: use incomingCallSid even if direction unknown
        callSid = deviceState.incomingCallSid;
        console.log('[AdminLayout] Using incomingCallSid as fallback:', callSid);
      }
      
      console.log('[AdminLayout] Extracted direction:', direction);
      console.log('[AdminLayout] Final callSid:', callSid);
      
      if (callSid) {
        const finalDirection: 'inbound' | 'outbound' = direction?.toLowerCase() === 'inbound' ? 'inbound' : 'outbound';
        console.log('[AdminLayout] ✓ Storing call info for post-work:', { callSid, direction: finalDirection });
        setPostWorkCallInfo({
          callSid,
          direction: finalDirection,
          startTime: Date.now()
        });
        
        // Update status
        if (finalDirection === 'inbound') {
          updateAgentStatus('active_on_inbound_call');
        } else {
          updateAgentStatus('active_on_outbound_call');
        }
      } else {
        console.error('[AdminLayout] ✗ Cannot store call info - no callSid available!');
        console.error('[AdminLayout] Will retry callSid extraction in 500ms...');
        
        // Retry after a short delay in case parameters aren't ready yet
        setTimeout(() => {
          const retryParams = (deviceState.activeCall?.parameters as any) || {};
          const retryCallSid = retryParams.CallSid || deviceState.incomingCallSid;
          const retryDirection = retryParams.Direction;
          
          console.log('[AdminLayout] Retry extraction - callSid:', retryCallSid, 'direction:', retryDirection);
          
          if (retryCallSid) {
            const finalDirection: 'inbound' | 'outbound' = retryDirection?.toLowerCase() === 'inbound' ? 'inbound' : 'outbound';
            console.log('[AdminLayout] ✓ Retry successful! Storing call info:', { callSid: retryCallSid, direction: finalDirection });
            setPostWorkCallInfo({
              callSid: retryCallSid,
              direction: finalDirection,
              startTime: Date.now()
            });
            
            // Update status if needed
            if (finalDirection === 'inbound') {
              updateAgentStatus('active_on_inbound_call');
            } else {
              updateAgentStatus('active_on_outbound_call');
            }
          } else {
            console.error('[AdminLayout] ✗ Retry failed - still no callSid available');
            console.error('[AdminLayout] This will cause post-work to fail. Please check Twilio Device configuration.');
          }
        }, 500);
      }
    }
    
    // Detect call disconnect: was connected, now idle
    if (previousState === 'connected' && currentState === 'idle' && !isInPostWork) {
      console.log('[AdminLayout] Call disconnected, entering post-work mode. postWorkCallInfo:', postWorkCallInfo);
      
      // Enter post-work mode immediately when call ends
      setIsInPostWork(true);
      updateAgentStatus('post_work_time');
    }
    
    // Update ref for next render
    previousCallStateRef.current = currentState;
  }, [deviceState.callState, deviceState.activeCall, deviceState.incomingCallSid, isInPostWork, postWorkCallInfo, user, agentInfo.id]);

  // Load menu from database
  useEffect(() => {
    // Don't load menu until user is authenticated
    if (!user) {
      setIsLoadingMenu(false);
      setNavCategories([]);
      return;
    }

    const loadMenu = async () => {
      try {
        setIsLoadingMenu(true);

        // Load categories
        const { data: categories, error: catError } = await supabase
          .from('menu_categories')
          .select('*')
          .eq('visible', true)
          .order('order', { ascending: true });

        if (catError) {
          console.error('Error loading categories:', catError);
          // Create default categories if they don't exist
          await createDefaultMenuStructure();
          return;
        }

        // Load pages
        const { data: pages, error: pageError } = await supabase
          .from('page_status')
          .select('*')
          .eq('visible', true)
          .order('order', { ascending: true });

        if (pageError) {
          console.error('Error loading pages:', pageError);
          // Create default pages if they don't exist
          await createDefaultMenuStructure();
          return;
        }

        // Build menu structure
        const rawMenu: NavCategory[] = (categories || []).map((cat: MenuCategoryDB) => {
          const categoryPages = (pages || [])
            .filter((p: PageStatusDB) => p.category_id === cat.id)
            .map((p: PageStatusDB) => ({
              to: p.route,
              label: p.label || p.route,
              icon: getLucideIcon(p.icon),
            }));

          return {
            title: `${cat.emoji} ${cat.name}`,
            items: categoryPages,
          };
        }).filter(cat => cat.items.length > 0); // Only show categories with visible pages

        // Filter menu based on permissions
        const filteredMenu = filterMenuByPermissions(rawMenu);
        setNavCategories(filteredMenu);
        
        // Categories start collapsed by default (no auto-expand)

        // Also update page statuses
        const statusMap = new Map<string, 'red' | 'yellow' | 'green'>();
        (pages || []).forEach((p: PageStatusDB) => {
          statusMap.set(p.route, p.status);
        });
        setPageStatuses(statusMap);

        console.log('Menu loaded from database:', filteredMenu.length, 'categories');
      } catch (err) {
        console.error('Error loading menu:', err);
        // Fallback to hardcoded menu
        const fallbackMenu: NavCategory[] = [
          {
            title: '🏠 Dashboard & System',
            items: [
              { to: '/dashboard', label: 'Dashboard', icon: getLucideIcon('Activity') },
              { to: '/system-overview', label: 'System Overview', icon: getLucideIcon('Settings') },
            ]
          },
          {
            title: '📞 Contact Center',
            items: [
              { to: '/contact-center/agent-console', label: 'Agent Console', icon: getLucideIcon('Phone') },
              { to: '/contact-center/queue-management', label: 'Queue Management', icon: getLucideIcon('ListChecks') },
            ]
          },
          {
            title: '🤖 AI Voice Agents',
            items: [
              { to: '/ai-voice-agents/phone-numbers', label: 'Phone Numbers', icon: getLucideIcon('Phone') },
              { to: '/ai-voice-agents/knowledge-base', label: 'Knowledge Base', icon: getLucideIcon('BookOpen') },
            ]
          }
        ];
        setNavCategories(fallbackMenu);
      } finally {
        setIsLoadingMenu(false);
      }
    };

    loadMenu();
    
    // Subscribe to menu changes
    const categoriesChannel = supabase
      .channel('menu_categories_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'menu_categories'
        },
        (payload) => {
          console.log('Menu categories changed:', payload);
          loadMenu();
        }
      )
      .subscribe();
    
    const pagesChannel = supabase
      .channel('page_status_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'page_status'
        },
        (payload) => {
          console.log('Page status changed:', payload);
          loadMenu();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(categoriesChannel);
      supabase.removeChannel(pagesChannel);
    };
  }, [user]); // Reload menu when user changes (includes initial auth)

  // Filter menu based on user permissions
  const filterMenuByPermissions = (menu: NavCategory[]): NavCategory[] => {
    console.log('[AdminLayout] filterMenuByPermissions called');
    console.log('[AdminLayout] user from useAuth:', user);
    console.log('[AdminLayout] permissionsLoading:', permissionsLoading);
    console.log('[AdminLayout] isAdmin() result:', isAdmin());
    console.log('[AdminLayout] raw menu length:', menu.length);
    
    // Wait for permissions to load before filtering
    if (permissionsLoading) {
      console.log('[AdminLayout] Permissions still loading, returning full menu temporarily');
      return menu;
    }
    
    // Admins see everything
    if (isAdmin()) {
      console.log('[AdminLayout] User is admin, returning full menu');
      return menu;
    }

    // Filter menu items based on permissions
    const filteredMenu = menu.map(category => ({
      ...category,
      items: category.items.filter(item => {
        const requiredPermissions = getRoutePermissions(item.to);
        
        // No permission required = allow
        if (!requiredPermissions) {
          return true;
        }
        
        // Check permission(s)
        if (Array.isArray(requiredPermissions)) {
          return hasAnyPermission(requiredPermissions);
        }
        
        return hasPermission(requiredPermissions);
      })
    })).filter(category => category.items.length > 0); // Remove empty categories
    
    // Ensure at least the home page is always visible
    if (filteredMenu.length === 0) {
      console.log('[AdminLayout] Filtered menu is empty, returning fallback home menu');
      return [{
        title: '🏠 Dashboard',
        items: [
          { to: '/', label: 'Home', icon: getLucideIcon('Home') }
        ]
      }];
    }
    
    console.log('[AdminLayout] Returning filtered menu with', filteredMenu.length, 'categories');
    return filteredMenu;
  };

  // Load agent info and queue assignments
  useEffect(() => {
    const loadAgent = async () => {
      if (user) {
        // Load or create agent bound to this user in consolidated agents table
        let agent: any = null;
        const { data: foundAgent, error: findErr } = await supabase
          .from('agents')
          .select('id, name, email, current_status')
          .eq('user_id', user.id)
          .single();

        if (findErr && (findErr as any).code !== 'PGRST116') {
          console.error('[AdminLayout] Error loading agent:', findErr);
        }

        if (!foundAgent) {
          const defaultName = (user as any).user_metadata?.full_name || user.email?.split('@')[0] || 'Agent';
          const { data: newAgent, error: insertErr } = await supabase
            .from('agents')
            .insert({
              user_id: user.id,
              name: defaultName,
              email: user.email,
              phone_number: null,
              current_status: 'available',
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select('id, name, email, current_status')
            .single();

          if (insertErr) {
            console.error('[AdminLayout] Failed to create agent for user:', insertErr);
          }
          agent = newAgent;
        } else {
          agent = foundAgent;
        }

        if (agent) {
          // Normalize and persist default: if missing or 'offline', set to 'available'
          if (!agent.current_status || agent.current_status === 'offline') {
            await supabase
              .from('agents')
              .update({ current_status: 'available', updated_at: new Date().toISOString() })
              .eq('user_id', user.id);
            agent.current_status = 'available';
          }

          setAgentInfo({
            id: agent.id,
            name: agent.name || user.email?.split('@')[0] || 'User',
            status: ((agent.current_status as any) && (agent.current_status as any) !== 'offline'
              ? (agent.current_status as any)
              : 'available')
          });

          // Initialize Twilio Device for this agent (always ready)
          // Only initialize if not already initialized by AgentConsole
          if (deviceState.status === 'idle') {
            console.log('[AdminLayout] Initializing Twilio Device for agent:', agent.id);
            deviceActions.initializeDevice(`agent-${agent.id}`);
          }

          // Load agent's queue assignments
          const { data: assignments } = await supabase
            .from('agent_queue_assignments')
            .select(`
              id,
              queue_id,
              call_queues (
                id,
                name,
                company_name,
                is_active
              )
            `)
            .eq('agent_id', agent.id);

          if (assignments) {
            const queues = assignments
              .map((a: any) => a.call_queues)
              .filter((q: any) => q && q.is_active);
            setAgentQueues(queues);
          }
        }
      }
    };
    loadAgent();

    // Subscribe to status changes and queue assignments from database
    if (!user) return;

    const statusChannel = supabase
      .channel('agent_status_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'agents',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[AdminLayout] Agent row changed:', payload.new);
          const newStatus = (payload.new as any).current_status;
          if (newStatus !== undefined) {
            const normalized = newStatus && newStatus !== 'offline' ? newStatus : 'available';
            setAgentInfo(prev => ({
              ...prev,
              status: normalized as any
            }));
          }
        }
      )
      .subscribe();

    // Subscribe to queue assignment changes
    let queueChannel: any = null;
    if (agentInfo.id) {
      queueChannel = supabase
        .channel('agent_queue_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'agent_queue_assignments',
            filter: 'agent_id=eq.' + agentInfo.id,
          },
          async () => {
            // Reload queue assignments when they change
            const { data: assignments } = await supabase
              .from('agent_queue_assignments')
              .select(`
                id,
                queue_id,
                call_queues (
                  id,
                  name,
                  company_name,
                  is_active
                )
              `)
              .eq('agent_id', agentInfo.id);

            if (assignments) {
              const queues = assignments
                .map((a: any) => a.call_queues)
                .filter((q: any) => q && q.is_active);
              setAgentQueues(queues);
            }
          }
        )
        .subscribe();
    }

    return () => {
      supabase.removeChannel(statusChannel);
      if (queueChannel) {
        supabase.removeChannel(queueChannel);
      }
    };
  }, [user, agentInfo.id]);

  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
      navigate('/login');
    }
  };

  const handleAcceptCall = () => {
    deviceActions.acceptCall();
  };

  const handleRejectCall = () => {
    deviceActions.rejectCall();
  };

  const changeStatus = async (newStatus: typeof agentInfo.status) => {
    setAgentInfo(prev => ({ ...prev, status: newStatus }));
    setShowStatusDropdown(false);
    
    if (user) {
      // Persist normalized status to consolidated agents table
      const dbStatus = (newStatus === 'dialing' || newStatus === 'active_on_inbound_call' || newStatus === 'active_on_outbound_call' || newStatus === 'post_work_time')
        ? 'busy'
        : (newStatus as any);
      await supabase
        .from('agents')
        .update({ current_status: dbStatus, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
    }
  };

  const handleMakeCall = async (number: string, callSource: { type: string; id?: string; name: string }) => {
    if (!user || !agentInfo.id) return;
    
    console.log('[AdminLayout] handleMakeCall called');
    console.log('[AdminLayout] Device status:', deviceState.status);
    console.log('[AdminLayout] Call state:', deviceState.callState);
    console.log('[AdminLayout] Device object exists:', !!deviceState.device);
    
    // Check if device is ready - but provide more helpful error
    if (deviceState.status !== 'ready') {
      console.error('[AdminLayout] Device not ready. Status:', deviceState.status);
      console.error('[AdminLayout] Device details:', { 
        status: deviceState.status, 
        callState: deviceState.callState,
        error: deviceState.error,
        hasDevice: !!deviceState.device
      });
      
      // Try to reinitialize if device is in idle/error state
      if (deviceState.status === 'idle' || deviceState.status === 'error') {
        console.log('[AdminLayout] Attempting to reinitialize device...');
        alert('Initializing phone system... Please try again in a moment.');
        deviceActions.initializeDevice(`agent-${agentInfo.id}`);
        return;
      }
      
      alert(`Phone system not ready (Status: ${deviceState.status}). ${deviceState.error || 'Please wait a moment and try again.'}`);
      return;
    }
    
    // Check if already on a call
    if (deviceState.callState !== 'idle') {
      console.error('[AdminLayout] Already on a call. State:', deviceState.callState);
      alert('Already on a call. Please hang up before making another call.');
      return;
    }
    
    // Set status to "dialing" when popup opens and call is initiated
    console.log('[AdminLayout] Setting status to dialing before making call to:', number);
    await supabase
      .from('agents')
      .update({ current_status: 'busy', updated_at: new Date().toISOString() })
      .eq('user_id', user.id);
    
    setAgentInfo(prev => ({ ...prev, status: 'dialing' }));
    
    // Make the call and handle errors
    try {
      console.log('[AdminLayout] Initiating call via Twilio...');
      await deviceActions.makeCall(number, { agentId: agentInfo.id });
      console.log('[AdminLayout] Call initiated successfully');
    } catch (error) {
      console.error('[AdminLayout] Failed to make call:', error);
      alert(`Failed to make call: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Revert status back to available on error
      await supabase
        .from('agents')
        .update({ current_status: 'available', updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      
      setAgentInfo(prev => ({ ...prev, status: 'available' }));
    }
    
    // The status will automatically change to 'active_on_outbound_call' 
    // when the call connects via the useEffect hook
  };

  const handleSaveCustomerDetails = async (data: any) => {
    try {
      const callSid = (deviceState.activeCall?.parameters?.CallSid || deviceState.incomingCallSid) as string | undefined;
      const phoneNumber = (() => {
        const p = (deviceState.activeCall?.parameters as any) || {};
        const from = p?.From as string | undefined;
        const to = p?.To as string | undefined;
        const dir = p?.Direction as string | undefined;
        const isClient = (v?: string) => (v?.startsWith?.('client:') ?? false);
        if (dir === 'inbound') return isClient(from) ? to : from;
        if (dir === 'outbound') return isClient(to) ? from : to;
        return !isClient(from) ? from : (!isClient(to) ? to : undefined);
      })();

      const payload = {
        call_sid: callSid || null,
        phone: data.phone || phoneNumber || null,
        name: data.name || null,
        email: data.email || null,
        address: data.address || null,
        active_agreement: data.activeAgreement || null,
        last_service_date: data.lastServiceDate || null,
        last_service_call_type: data.lastServiceCallType || null,
        created_at: new Date().toISOString()
      };
      const { error } = await supabase.from('pre_contacts').insert(payload);
      if (error) {
        console.error('[AdminLayout][PreContact] insert failed:', error.message);
      }
    } catch (e) {
      console.error('[AdminLayout][PreContact] save error:', e);
    }
  };

  const statusColors: Record<typeof agentInfo.status, string> = {
    available: '#10b981',
    busy: '#ef4444',
    away: '#6b7280',
    break: '#f59e0b',
    meal: '#f97316',
    meeting: '#8b5cf6',
    training: '#3b82f6',
    active_on_inbound_call: '#06b6d4', // cyan
    active_on_outbound_call: '#0ea5e9', // sky blue
    post_work_time: '#f59e0b', // amber
    dialing: '#f59e0b' // amber (same as post_work_time)
  };

  const toggleCategory = (title: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
      <div className="grid min-h-screen md:grid-cols-[280px_1fr]">
        <aside className="border-r flex flex-col" style={{ backgroundColor: theme.colors.bgCard, borderColor: theme.colors.borderColor }}>
          <div className="h-24 px-6 flex flex-col justify-center border-b" style={{ borderColor: theme.colors.borderColor }}>
            <div className="text-xs mb-1" style={{ color: theme.colors.textSecondary }}>
              Logged in as <span className="font-medium" style={{ color: theme.colors.textPrimary }}>
                {agentInfo.name || user?.email?.split('@')[0] || 'User'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs mb-1">
              <span style={{ color: theme.colors.textSecondary }}>Status:</span>
              <span 
                className="font-medium capitalize px-2 py-0.5 rounded"
                style={{ 
                  backgroundColor: `${statusColors[agentInfo.status]}20`,
                  color: statusColors[agentInfo.status]
                }}
              >
                {agentInfo.status}
              </span>
            </div>
            {agentQueues.length > 0 && (
              <div className="text-xs" style={{ color: theme.colors.textSecondary }}>
                Queues: <span className="font-medium" style={{ color: theme.colors.textPrimary }}>
                  {agentQueues.map(q => q.name).join(', ')}
                </span>
              </div>
            )}
          </div>

          <nav className="flex flex-col gap-2 px-4 pb-6 flex-1 overflow-y-auto">
            {isLoadingMenu ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
                  Loading menu...
                </div>
              </div>
            ) : navCategories.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
                  No menu items configured
                </div>
              </div>
            ) : (
              navCategories.map((category) => (
              <div key={category.title} className="space-y-1">
                <button
                  onClick={() => toggleCategory(category.title)}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
                  style={{ color: theme.colors.textTertiary }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textTertiary}
                >
                  {category.title}
                  {expandedCategories.has(category.title) ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                </button>

                {expandedCategories.has(category.title) && (
                  <div className="space-y-1 ml-2">
                    {category.items.map((item) => {
                      const Icon = item.icon;
                      const status = pageStatuses.get(item.to);
                      const statusColor = status === 'green' ? '#10b981' : status === 'yellow' ? '#f59e0b' : status === 'red' ? '#ef4444' : undefined;
                      
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={true}
                          className={({ isActive }) =>
                            clsx(
                              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                              isActive ? 'shadow-inner' : ''
                            )
                          }
                          style={({ isActive }: { isActive: boolean }) => ({
                            backgroundColor: isActive ? theme.colors.primaryColor : 'transparent',
                            color: isActive ? '#ffffff' : theme.colors.textSecondary,
                          })}
                          onMouseEnter={(e) => {
                            const target = e.currentTarget as HTMLAnchorElement;
                            const isActive = target.getAttribute('aria-current') === 'page';
                            if (!isActive) {
                              target.style.backgroundColor = theme.colors.bgHover;
                            }
                          }}
                          onMouseLeave={(e) => {
                            const target = e.currentTarget as HTMLAnchorElement;
                            const isActive = target.getAttribute('aria-current') === 'page';
                            if (!isActive) {
                              target.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {statusColor && (
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: statusColor }}
                              title={`Status: ${status}`}
                            />
                          )}
                          <Icon size={16} />
                          {item.label}
                          {'badge' in item && (item as any).badge && (
                            <span className="ml-auto text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                              {(item as any).badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
              ))
            )}
          </nav>

          <div className="border-t p-4" style={{ borderColor: theme.colors.borderColor }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.colors.bgHover }}>
                <User size={16} style={{ color: theme.colors.textSecondary }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: theme.colors.textPrimary }}>
                  {user?.email || 'Admin User'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md transition-colors"
              style={{ color: theme.colors.textSecondary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = theme.colors.textPrimary;
                e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = theme.colors.textSecondary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </aside>

        <main className="flex flex-col">
          <header className="sticky top-0 z-10 flex h-24 items-center justify-between border-b px-6 shadow-sm" style={{ backgroundColor: theme.colors.bgCard, borderColor: theme.colors.borderColor }}>
            <div>
              <CallConversionGauges />
            </div>
            
            <div className="flex items-center gap-4">
              {/* Incoming Call Alert */}
              {deviceState.callState === 'ringing' && (
                <div className="flex items-center gap-3 px-4 py-2 rounded-md animate-pulse" style={{ backgroundColor: theme.colors.infoBg, border: `2px solid ${theme.colors.infoBorder}` }}>
                  <PhoneIncoming size={18} style={{ color: theme.colors.infoText }} />
                  <div>
                    <div className="text-sm font-bold" style={{ color: theme.colors.textPrimary }}>Incoming Call</div>
                    
                    {/* Company Name */}
                    <div className="text-sm font-semibold" style={{ color: theme.colors.textPrimary }}>
                      {deviceState.companyName || 'Unknown Company'}
                    </div>
                    
                    {/* Queue Name */}
                    <div className="text-xs" style={{ color: theme.colors.textSecondary }}>
                      Queue: {deviceState.queueName || 'Unknown Queue'}
                    </div>
                    
                    {/* Caller Phone Number */}
                    <div className="text-xs" style={{ color: theme.colors.textSecondary }}>
                      From: {deviceState.activeCall?.parameters?.From || 'Unknown'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAcceptCall}
                      className="px-3 py-1 rounded text-xs font-bold text-white transition-colors"
                      style={{ backgroundColor: '#10b981' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                    >
                      Accept
                    </button>
                    <button
                      onClick={handleRejectCall}
                      className="px-3 py-1 rounded text-xs font-bold text-white transition-colors"
                      style={{ backgroundColor: '#ef4444' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {/* Call Status Indicator - Simple status without controls (controls moved to slide-in panel) */}
              {(deviceState.callState === 'connected' || deviceState.callState === 'connecting') && (
                <div className="flex items-center gap-3 px-4 py-2 rounded-md" style={{ backgroundColor: theme.colors.successBg, border: `2px solid ${theme.colors.successBorder}` }}>
                  <Phone size={18} style={{ color: theme.colors.successText }} />
                  <div>
                    <div className="text-sm font-bold" style={{ color: theme.colors.textPrimary }}>
                      {deviceState.callState === 'connecting' ? 'Connecting...' : 'Call Active'}
                    </div>
                    <div className="text-xs" style={{ color: theme.colors.textSecondary }}>
                      {deviceState.activeCall?.parameters?.To || deviceState.activeCall?.parameters?.From || 'In progress'}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Agent Status & Controls */}
              {deviceState.callState !== 'ringing' && (
                <div className="flex items-center gap-3">
                  {/* DEBUG: Temporary button to test slide-out */}
                  <button
                    onClick={() => setDebugPanelOpen(!debugPanelOpen)}
                    className="flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium text-white transition-colors"
                    style={{ backgroundColor: debugPanelOpen ? '#ef4444' : '#8b5cf6' }}
                  >
                    🔧 {debugPanelOpen ? 'Close' : 'Open'} Panel
                  </button>
                  {/* Status Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      className="flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                      style={{ 
                        backgroundColor: `${statusColors[agentInfo.status]}20`,
                        color: statusColors[agentInfo.status]
                      }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: statusColors[agentInfo.status] }}
                      />
                      <span className="capitalize">{agentInfo.status}</span>
                      <ChevronDown size={12} />
                    </button>
                    
                    {showStatusDropdown && (
                      <div 
                        className="absolute top-full mt-1 right-0 rounded-md shadow-lg py-1 z-50 min-w-32"
                        style={{ 
                          backgroundColor: theme.colors.bgCard,
                          border: `1px solid ${theme.colors.borderColor}`
                        }}
                      >
                        {(['available', 'busy', 'away', 'break', 'meal', 'meeting', 'training'] as const).map((status) => (
                          <button
                            key={status}
                            onClick={() => changeStatus(status)}
                            className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors"
                            style={{ color: theme.colors.textPrimary }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: statusColors[status] }}
                            />
                            <span className="capitalize">{status}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Make Call Button */}
                  <button
                    onClick={() => setShowMakeCallModal(true)}
                    className="flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium text-white transition-colors"
                    style={{ backgroundColor: theme.colors.primaryColor }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <Phone size={14} />
                    Make Call
                  </button>

                  {/* Phone Status Indicator - only show when device is ready */}
                  {deviceState.status === 'ready' && (
                    <div className="flex items-center gap-2 px-3 py-1 rounded-md" style={{ backgroundColor: theme.colors.bgHover }}>
                      <Phone size={14} style={{ color: theme.colors.successText }} />
                      <span className="text-xs font-medium" style={{ color: theme.colors.textSecondary }}>
                        Phone: <span style={{ color: theme.colors.successText }}>
                          {deviceState.callState === 'idle' && 'Ready'}
                          {deviceState.callState === 'connecting' && 'Connecting...'}
                          {deviceState.callState === 'connected' && 'On Call'}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </header>
          
          <div className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: theme.colors.bgPage }}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Make Call Modal */}
      <MakeCallModal
        isOpen={showMakeCallModal}
        onClose={() => setShowMakeCallModal(false)}
        onMakeCall={handleMakeCall}
        agentId={agentInfo.id}
      />

      {/* Live Coaching Panel - Slides out during active calls and post-work */}
      <LiveCoachingPanel
        isActive={deviceState.callState === 'connected' || deviceState.callState === 'connecting' || isInPostWork || debugPanelOpen || deviceState.isOnHold}
        isPostWork={isInPostWork}
        transcriptChunks={realtimeTranscripts}
        liveSuggestions={liveSuggestions}
        customerDetails={{
          phone: (() => {
            const params = (deviceState.activeCall?.parameters as any) || {};
            const from = params?.From as string | undefined;
            const to = params?.To as string | undefined;
            const direction = params?.Direction as string | undefined;
            const isClient = (v?: string) => v?.startsWith?.('client:') ?? false;
            
            if (direction === 'inbound') return isClient(from) ? to : from;
            if (direction === 'outbound') return isClient(to) ? from : to;
            return !isClient(from) ? from : (!isClient(to) ? to : undefined);
          })(),
          direction: postWorkCallInfo?.direction || (deviceState.activeCall?.parameters as any)?.Direction || 'inbound'
        }}
        onSaveCustomerDetails={handleSaveCustomerDetails}
        callSid={parentSid || childSid || undefined}
        agentId={agentInfo.id || undefined}
        onHangUp={() => {
          // Set post-work state BEFORE hanging up so panel stays open
          if (postWorkCallInfo && !isInPostWork) {
            console.log('[AdminLayout] Entering post-work mode before hangup');
            setIsInPostWork(true);
            
            // Update agent status to post-work
          if (user && agentInfo.id) {
            supabase
              .from('agents')
              .update({ current_status: 'busy', updated_at: new Date().toISOString() })
              .eq('user_id', user.id);
            
            setAgentInfo(prev => ({ ...prev, status: 'post_work_time' }));
          }
          }
          
          // Now hang up the call
          deviceActions.hangUp();
        }}
        onTransferCall={(type, target) => {
          console.log('[AdminLayout] Transfer requested:', { type, target, callSid: parentSid || childSid });
          
          // TODO: Implement actual transfer logic via Twilio API
          // For now, just log the transfer request
          alert(`Transfer functionality coming soon!\nType: ${type}\nTarget: ${target}`);
        }}
        onRequestOutcomeSelection={() => {
          // Agent clicked "Post Work Completed" - close panel and show outcome modal
          console.log('[AdminLayout] Agent requested outcome selection');
          setShowOutcomeSelection(true);
        }}
        onPostWorkComplete={async (outcome: 'positive' | 'negative' | 'neutral') => {
          console.log('[AdminLayout] onPostWorkComplete called with outcome:', outcome);
          console.log('[AdminLayout] postWorkCallInfo:', postWorkCallInfo);
          console.log('[AdminLayout] user:', user?.id);
          console.log('[AdminLayout] Current isInPostWork:', isInPostWork);
          
          if (!postWorkCallInfo || !user) {
            console.error('[AdminLayout] Missing required data - postWorkCallInfo or user');
            return;
          }
          
          console.log('[AdminLayout] Updating call record in database...');
          // Update call record with agent sentiment
          const { error } = await supabase
            .from('calls')
            .update({ 
              agent_sentiment: outcome,
              updated_at: new Date().toISOString()
            })
            .eq('call_sid', postWorkCallInfo.callSid);
          
          if (error) {
            console.error('[AdminLayout] Failed to update call with agent sentiment:', error);
          } else {
            console.log('[AdminLayout] Call updated successfully with agent sentiment:', outcome);
          }
          
          console.log('[AdminLayout] Resetting post-work state...');
          // Reset all post-work state
          setIsInPostWork(false);
          setShowOutcomeSelection(false);
          setPostWorkCallInfo(null);
          console.log('[AdminLayout] Post-work state reset complete. isInPostWork should now be false');
          
          console.log('[AdminLayout] Updating agent status to available...');
          // Return to available status
          const { error: statusError } = await supabase
            .from('agents')
            .update({ current_status: 'available', updated_at: new Date().toISOString() })
            .eq('user_id', user.id);
          
          if (statusError) {
            console.error('[AdminLayout] Failed to update agent status:', statusError);
          } else {
            console.log('[AdminLayout] Agent status updated in database');
          }
          
          setAgentInfo(prev => ({ ...prev, status: 'available' }));
          console.log('[AdminLayout] Agent status updated in local state');
          console.log('[AdminLayout] onPostWorkComplete complete - panel should close now');
        }}
        isMuted={deviceState.isMuted}
        isOnHold={deviceState.isOnHold}
        onToggleMute={deviceActions.toggleMute}
        onToggleHold={deviceActions.toggleHold}
      />

    </div>
  );
};

export default AdminLayout;
